import axiosInstance from "./axios";

export interface RoomFilters {
  genre_ids?: number[];
  min_rating?: number;
  year_from?: number;
  year_to?: number;
}

export interface RoomParticipant {
  user_id: number;
  username: string;
  picture_url: string | null;
  joined_at: string;
  is_active: boolean;
  last_activity_at: string;
}

export interface RoomMovie {
  id: number;
  title: string;
  title_ru: string | null;
  title_ua: string | null;
  title_ar: string | null;
  poster_path: string;
  poster_path_ru: string | null;
  poster_path_ua: string | null;
  poster_path_ar: string | null;
  backdrop_path: string;
  release_date: string;
  vote_average: number;
  imdb_rating: number | null;
  kinopoisk_rating: number | null;
  rotten_tomatoes_rating: number | null;
  overview: string;
  overview_ru: string | null;
  overview_ua: string | null;
  overview_ar: string | null;
  genres: string;
  popularity: number;
  likes_count: number;
  shares_count: number;
  favorites_count: number;
  production_companies: string;
  trailer_key: string | null;
}

export interface RoomMatch {
  id: number;
  movie_id: number;
  movie_title: string;
  movie_poster_path: string;
  matched_at: string;
  is_selected: boolean;
  movie_detail: RoomMovie | null;
}

export interface RoomDetail {
  id: number;
  code: string;
  status: "waiting" | "active" | "completed" | "expired" | "cancelled";
  filters: RoomFilters;
  creator_id: number;
  creator_username: string;
  participants: RoomParticipant[];
  matches: RoomMatch[];
  created_at: string;
  expires_at: string;
  started_at: string | null;
}

export interface RoomMoviesResponse {
  results: RoomMovie[];
  has_more: boolean;
  offset: number;
}

export interface SwipeResult {
  match: boolean;
  match_id?: number;
  movie_id?: number;
  movie_title?: string;
}

export interface RoomStatus {
  status: string;
  participants: RoomParticipant[];
  matches: RoomMatch[];
  updated_at: string;
}

const roomApi = {
  create: (filters: RoomFilters = {}) =>
    axiosInstance.post<RoomDetail>("/rooms/", filters),

  getDetail: (code: string) =>
    axiosInstance.get<RoomDetail>(`/rooms/${code}/`),

  join: (code: string) =>
    axiosInstance.post<RoomDetail>(`/rooms/${code}/join/`),

  start: (code: string) =>
    axiosInstance.post<RoomDetail>(`/rooms/${code}/start/`),

  getMovies: (code: string, batchSize = 10) =>
    axiosInstance.get<RoomMoviesResponse>(
      `/rooms/${code}/movies/?batch_size=${batchSize}`
    ),

  getMoviesAtOffset: (code: string, offset: number, batchSize = 10) =>
    axiosInstance.get<RoomMoviesResponse>(
      `/rooms/${code}/movies/?offset=${offset}&batch_size=${batchSize}`
    ),

  swipe: (code: string, movieId: number, action: "like" | "dislike") =>
    axiosInstance.post<SwipeResult>(`/rooms/${code}/swipe/`, {
      movie_id: movieId,
      action,
    }),

  getMatches: (code: string) =>
    axiosInstance.get<RoomMatch[]>(`/rooms/${code}/matches/`),

  selectMatch: (code: string, matchId: number) =>
    axiosInstance.post<RoomMatch>(`/rooms/${code}/select/${matchId}/`),

  close: (code: string) =>
    axiosInstance.post(`/rooms/${code}/close/`),

  getStatus: (code: string) =>
    axiosInstance.get<RoomStatus>(`/rooms/${code}/status/`),
};

export default roomApi;
